import axios from "axios";
import React, { useState, useEffect, useContext, useMemo, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useParams } from "react-router-dom";
import Loading from "../../common/loading";
import Errors from "../../common/error";
import Breadcrumbs from "../../common/breadcumb";
import "./news.scss";
import { LangContext } from "../../context/LangContext";

function NewsDetail() {
  const { state } = useContext(LangContext);
  const nep = state.np;
  const { category, slug } = useParams();
  const lightboxRef = useRef(null);
  const prevFocusRef = useRef(null);

  const catUrl = category === "news-&-events" ? "news" : category;
  const API_URL = `${process.env.REACT_APP_URL}${catUrl}/detail/${slug}`;

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [lightboxImg, setLightboxImg] = useState(null);
  const [imgLoading, setImgLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(false);

      try {
        const response = await axios.get(API_URL);
        setDetail(response.data.detail);
      } catch (err) {
        console.error(err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [API_URL]);

  const mainData = useMemo(() => {
    if (!detail) return null;
    return nep ? detail.np : detail.en;
  }, [detail, nep]);

  const openLightbox = useCallback((src) => {
    prevFocusRef.current = document.activeElement;
    setImgLoading(true);
    setLightboxImg(src);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxImg(null);
    setImgLoading(false);
  }, []);

  useEffect(() => {
    if (!lightboxImg) return;
    document.body.style.overflow = "hidden";
    const handleKey = (e) => {
      if (e.key === "Escape") {
        closeLightbox();
        return;
      }
      if (e.key === "Tab" && lightboxRef.current) {
        const focusable = lightboxRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", handleKey);
    setTimeout(() => {
      if (lightboxRef.current) {
        const btn = lightboxRef.current.querySelector("button");
        if (btn) btn.focus();
      }
    }, 100);
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
      if (prevFocusRef.current && prevFocusRef.current.focus) {
        prevFocusRef.current.focus();
      }
    };
  }, [lightboxImg, closeLightbox]);

  const handleKeyClick = useCallback((cb) => (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      cb();
    }
  }, []);

  if (loading) return <Loading />;
  if (error || !mainData) return <Errors />;

  const images = mainData.multi_images || [];

  return (
    <>
      <Breadcrumbs PageTitle={mainData.title} />

      <main className="InsidePageContainer">
        <div className="faq-box">
          <div className="wrapper">
            <div className="detail-parent">
              {mainData.image && (
                <div
                  className="detail-hero"
                  onClick={() => openLightbox(mainData.image)}
                  onKeyDown={handleKeyClick(() => openLightbox(mainData.image))}
                  role="button"
                  tabIndex={0}
                  aria-label={`${mainData.title} - ${nep ? "ठूलो देख्नुहोस्" : "View full image"}`}
                >
                  <div className="detail-hero-overlay">
                    <span className="detail-hero-icon">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                        <line x1="11" y1="8" x2="11" y2="14" />
                        <line x1="8" y1="11" x2="14" y2="11" />
                      </svg>
                    </span>
                  </div>
                  <img
                    src={mainData.image}
                    alt={mainData.image_alt || mainData.title}
                    className="detail-hero-img"
                  />
                </div>
              )}

              {images.length > 0 && (
                <div className="detail-gallery">
                  <h2 className="detail-gallery-title">
                    {nep ? "ग्यालरी" : "Gallery"}
                  </h2>
                  <div className="detail-gallery-grid">
                    {images.map((item, index) => (
                      <div
                        className="detail-gallery-item"
                        key={item.id || index}
                        onClick={() => openLightbox(item.doc)}
                        onKeyDown={handleKeyClick(() => openLightbox(item.doc))}
                        role="button"
                        tabIndex={0}
                        aria-label={`${mainData.title} ${nep ? "ग्यालरी" : "gallery"} ${index + 1} - ${nep ? "ठूलो देख्नुहोस्" : "view full image"}`}
                      >
                        <div className="detail-gallery-overlay">
                          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                            <circle cx="11" cy="11" r="8" />
                            <line x1="21" y1="21" x2="16.65" y2="16.65" />
                            <line x1="11" y1="8" x2="11" y2="14" />
                            <line x1="8" y1="11" x2="14" y2="11" />
                          </svg>
                        </div>
                        <img
                          src={item.doc}
                           alt={item.image_alt || item.file_alt || `${mainData.title} - ${index + 1}`}
                          className="detail-gallery-img"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="detail-content">
                <h2 className="detail-title">{mainData.title}</h2>
                <div
                  className="detail-description"
                  dangerouslySetInnerHTML={{
                    __html: mainData.description || "",
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </main>

      {lightboxImg && createPortal(
        <div
          className="detail-lightbox"
          ref={lightboxRef}
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label={mainData?.title || "Image"}
        >
          <div className="detail-lightbox-inner" onClick={(e) => e.stopPropagation()}>
            <button
              className="detail-lightbox-close"
              onClick={closeLightbox}
              aria-label={nep ? "बन्द गर्नुहोस्" : "Close"}
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            {imgLoading && <div className="detail-lightbox-loader" />}
            <img
              src={lightboxImg}
               alt={mainData?.image_alt || mainData?.title || ""}
              className="detail-lightbox-img"
              style={{ display: imgLoading ? "none" : "block" }}
              onLoad={() => setImgLoading(false)}
            />
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

export default NewsDetail;
